import React from 'react';
import { Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { EventBus } from '@/shared/utils';
import { Catalog as CatalogDict } from '@/shared/i18n';
import { CourseType } from '../../types/course';
import { CourseDataFilters } from '../../types/filtered-data';
import { Instrument } from '../../types/instrument';
import { instrumentHasChildren, getChildInstruments } from '../../utils/common';
import { FilterAccordion } from './filter-accordion';
import { FilterHeader } from './filter-header';
import { LevelSlider } from './filter-level-slider';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';

interface FilterContainerProps {
  dict: CatalogDict;
  eventBus: EventBus<any>;
  filters: {
    instruments: Instrument[];
    sidebar: {
      skillLevels: any[];
      genres: { name: string }[];
      topics: { name: string }[];
      type: CourseType[];
    };
  };
  activeFilters: CourseDataFilters;
  instrumentsFilterOnChange: (checked: boolean, instrument: Instrument) => void;
  handleSkillLevelsChange: (selectedLevels: string[] | null) => void;
  handleGenresChange: (genre: string, checked: boolean) => void;
  handleTopicsChange: (topic: string, checked: boolean) => void;
  handleTypeChange: (type: string, checked: boolean) => void;
  isEnrichingData?: boolean;
  onResetSidebarFilters: () => void;
  isAnyFilterActive: boolean;
}

export const FilterContainer = ({
  dict,
  filters,
  activeFilters,
  instrumentsFilterOnChange,
  handleSkillLevelsChange,
  handleGenresChange,
  handleTopicsChange,
  handleTypeChange,
  onResetSidebarFilters,
  isAnyFilterActive,
}: FilterContainerProps) => {
  const selectedParentInstruments = filters?.instruments?.filter((inst) => {
    return (
      activeFilters.instruments.includes(inst.instrumentName) &&
      instrumentHasChildren(inst, filters.instruments)
    );
  });

  // Remove loading check - filters should always be available from SSG
  // Even if isEnrichingData is true, we should still show the filters
  return (
    <section className="flex flex-col rounded-xl bg-paper min-w-[264px] py-6 gap-6 static top-0">
      <FilterHeader />
      {selectedParentInstruments?.map((parent, idx) => {
        const childInstruments = getChildInstruments(parent, filters.instruments);
        return (
          <FilterAccordion
            key={idx}
            defaultExpanded={true}
            title={`Tipo ${parent?.instrumentName?.toLowerCase()}`}
            accordionClassName="px-2"
          >
            <FormGroup>
              {childInstruments.map((child, childIndex) => (
                <FormControlLabel
                  key={childIndex}
                  label={child?.instrumentName}
                  control={
                    <Checkbox
                      checked={activeFilters?.instruments?.includes(child?.instrumentName)}
                      onChange={(e) => instrumentsFilterOnChange(e.target.checked, child)}
                      disabled={false} // Filters should never be disabled
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>
        );
      })}
      {filters?.sidebar?.skillLevels?.length > 0 && (
        <LevelSlider
          className="px-6"
          onChange={handleSkillLevelsChange}
          selectedLevels={activeFilters?.skillLevels}
          disabled={false} // Filters should never be disabled
        />
      )}

      <div>
        {filters?.sidebar?.genres?.length > 0 && (
          <FilterAccordion title="Genere" accordionClassName="px-2">
            <FormGroup>
              {filters?.sidebar?.genres?.map((genre, index) => (
                <FormControlLabel
                  key={index}
                  label={genre.name}
                  control={
                    <Checkbox
                      checked={activeFilters?.genres?.includes(genre?.name)}
                      onChange={(e) => handleGenresChange(genre?.name, e.target.checked)}
                      disabled={false} // Filters should never be disabled
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>
        )}
        {filters?.sidebar?.topics?.length > 0 && (
          <FilterAccordion title="Argomento" accordionClassName="px-2">
            <FormGroup>
              {filters?.sidebar?.topics?.map((topic, index) => (
                <FormControlLabel
                  key={index}
                  label={topic?.name}
                  control={
                    <Checkbox
                      checked={activeFilters?.topics?.includes(topic?.name)}
                      onChange={(e) => handleTopicsChange(topic?.name, e.target.checked)}
                      disabled={false} // Filters should never be disabled
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>
        )}
        {filters?.sidebar?.type?.length > 0 && (
          <FilterAccordion title="Tipologia" accordionClassName="px-2">
            <FormGroup>
              {filters?.sidebar?.type?.map((t, index) => (
                <FormControlLabel
                  key={index}
                  label={adaptCourseTypeText(t)}
                  control={
                    <Checkbox
                      checked={activeFilters?.types?.includes(t)}
                      onChange={(e) => handleTypeChange(t, e.target.checked)}
                      disabled={false} // Filters should never be disabled
                    />
                  }
                />
              ))}
            </FormGroup>
          </FilterAccordion>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          size="medium"
          variant="contained"
          color="primary"
          className="mx-4"
          onClick={onResetSidebarFilters}
          disabled={!isAnyFilterActive}
        >
          <Typography variant="button-m">Annulla filtri</Typography>
        </Button>
      </div>
    </section>
  );
};
